import * as React from 'react';

import { Accept, DropzoneOptions, DropzoneState, ErrorCode, useDropzone } from 'react-dropzone';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { readableFileSize } from '@/utils/file-size';

interface DropzoneConfig {
  multiple?: boolean;
  accept?: Accept;
  maxFiles?: number;
  minSize?: number;
  maxSize?: number;
}

interface DropzoneContentValue extends DropzoneState {
  config: DropzoneConfig;
}

const DropzoneContext = React.createContext<DropzoneContentValue | null>(null);

interface DropzoneProps extends DropzoneOptions {
  children?: React.ReactNode;
}

const Dropzone: React.FC<DropzoneProps> = ({ children, ...opts }) => {
  const dropzone = useDropzone({ ...opts });
  const { multiple, accept, maxFiles, maxSize, minSize } = opts;

  return (
    <DropzoneContext.Provider
      value={{
        ...dropzone,
        config: { multiple, accept, maxFiles, maxSize, minSize },
      }}>
      {children}
    </DropzoneContext.Provider>
  );
};
Dropzone.displayName = 'Dropzone';

function useDropzoneValue() {
  const context = React.useContext(DropzoneContext);

  if (!context) {
    throw new Error('useDropzoneValue must be used within Dropzone');
  }

  return context;
}

const DropzoneContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, children, ...props }, ref) => {
  const { getRootProps, isDragAccept, isDragReject } = useDropzoneValue();
  const rootProps = getRootProps({ ...props });

  return (
    <div
      className={cn(
        'border-border relative mx-auto flex cursor-pointer flex-col gap-2 rounded-md border border-dashed p-6 transition-colors',
        isDragAccept ? '!border-default-blue !bg-default-blue/15' : isDragReject ? '!border-default-red !bg-default-red/15' : '',
        className,
      )}
      ref={ref}
      {...rootProps}>
      {children}
    </div>
  );
});
DropzoneContent.displayName = 'DropzoneContent';

const DropzoneTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(({ className, children, ...props }, ref) => {
  return (
    <h2 className={cn('text-xl font-semibold tracking-tight', className)} ref={ref} {...props}>
      {children}
    </h2>
  );
});
DropzoneTitle.displayName = 'DropzoneTitle';

const DropzoneInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => {
  const { getInputProps } = useDropzoneValue();
  const inputProps = getInputProps({ ...props });

  return <Input className={cn(className)} ref={ref} {...inputProps} />;
});
DropzoneInput.displayName = 'DropzoneInput';

const DropzoneDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, children, ...props }, ref) => {
  return (
    <p className={cn('text-secondary-text text-sm', className)} ref={ref} {...props}>
      {children}
    </p>
  );
});
DropzoneDescription.displayName = 'DropzoneDescription';

const DropzoneMessage = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, children, ...props }, ref) => {
  const { fileRejections, config } = useDropzoneValue();

  const errors = React.useMemo(
    () =>
      fileRejections.flatMap((fileRejection) =>
        fileRejection.errors.map((error) => {
          switch (error.code) {
            case ErrorCode.FileInvalidType: {
              if (!config.accept) {
                return '';
              }

              const accepts = Object.keys(config.accept).map((accept) => accept.replace(/[^a-zA-Z ]/g, ''));

              const fileExtension = fileRejection.file.name.split('.').pop();

              return `Accepts ${accepts.join(', ')} received ${fileExtension}.`;
            }

            case ErrorCode.FileTooLarge: {
              if (!config.maxSize) {
                return '';
              }

              const fileSizeLarge = readableFileSize(fileRejection.file.size);
              const maxSize = readableFileSize(config.maxSize);

              return `File is too large, weighing ${fileSizeLarge}. Max size is ${maxSize}.`;
            }

            case ErrorCode.FileTooSmall: {
              if (!config.minSize) {
                return '';
              }

              const fileSizeSmall = readableFileSize(fileRejection.file.size);
              const minSize = readableFileSize(config.minSize);

              return `File is too small, weighing ${fileSizeSmall}. Min size is ${minSize}.`;
            }

            case ErrorCode.TooManyFiles: {
              if (!config.maxFiles) {
                return '';
              }

              const { length: fileLength } = fileRejections;
              const { maxFiles } = config;

              return `Too many files, maximum files is ${maxFiles} recevies ${fileLength}.`;
            }

            default:
              return 'Something went wrong upon reading the file.';
          }
        }),
      ),
    [fileRejections, config],
  );

  const body = errors.length ? String(errors[0]) : children;

  if (!body) {
    return null;
  }

  return (
    <p className={cn('text-default-red text-sm', className)} ref={ref} {...props}>
      {body}
    </p>
  );
});
DropzoneMessage.displayName = 'DropzoneMessage';

export { Dropzone, DropzoneContent, DropzoneTitle, DropzoneInput, DropzoneDescription, DropzoneMessage, useDropzoneValue };
